/**
 * Cart Loader — skeleton-shimmer overlay for cart MUTATIONS only.
 *
 * Scope after the bundle-cart consolidation:
 *   - <cart-items-component> (rendered in BOTH the drawer and the cart page)
 *     gets a full-coverage skeleton-shimmer overlay while /cart/change.js /
 *     /cart/update.js requests are in flight. This matters during the
 *     bundle-cart.js cascade removal, where several sequential change requests
 *     fire and we want the items area locked so the customer can't
 *     double-click mid-cascade. Overlay markup is built in _buildOverlay();
 *     styles are in cart-loader.css (.cart-skeleton*).
 *
 * What this file NO LONGER does (moved to assets/bundle-cart.js):
 *   - The add-to-cart BUTTON spinner. bundle-cart.js owns the one-and-only ATC
 *     loading state via setButtonLoading(), styled by bundle-cart.css
 *     (.bundle-atc-spinner).
 *   - Any /cart/add counting. Adds are handled entirely by bundle-cart.js.
 *
 * We patch window.fetch with a URL-based counter for mutations only. This is a
 * read-only observer of cart mutations (it never rewrites requests or cart
 * state), so it does not compete with bundle-cart.js for the cart-write path.
 */
(function () {
  'use strict';

  if (window.__cartLoaderInstalled) return;
  window.__cartLoaderInstalled = true;

  const CART_MUTATE_RE = /\/cart\/(change|update|clear)(\.js)?(\?|$)/;
  const RELEASE_DEBOUNCE_MS = 120;

  const SURFACE_SELECTOR = 'cart-items-component';

  class CartLoader {
    constructor() {
      this.activeMutations = 0;
      this.holdCount = 0; // explicit holds for long, multi-fetch ops (bundle cascade)
      this.surfaces = new WeakSet();
      this._hideTimer = null;
      this._observer = null;
      this._init();
    }

    _init() {
      this._refreshSurfaces();
      this._installFetchHook();
      this._observeMutations();
      document.addEventListener('cart:update', () => this._refreshSurfaces());
      document.addEventListener('shopify:section:load', () => this._refreshSurfaces());
    }

    _refreshSurfaces() {
      const els = document.querySelectorAll(SURFACE_SELECTOR);
      els.forEach((el) => this._ensureOverlay(el));
    }

    _ensureOverlay(el) {
      if (!el || this.surfaces.has(el)) {
        // Even if registered, make sure the overlay node is still present —
        // section reloads can swap the inner DOM out.
        if (el && !el.querySelector(':scope > .cart-loader-overlay')) {
          el.appendChild(this._buildOverlay());
        }
        return;
      }
      el.classList.add('cart-loader-host');
      el.appendChild(this._buildOverlay());
      this.surfaces.add(el);

      // If a mutation is already running when this surface mounts, light it up.
      if (this.activeMutations > 0) this._paintSurfaces(true);
    }

    _buildOverlay() {
      const overlay = document.createElement('div');
      overlay.className = 'cart-loader-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.dataset.active = 'false';

      // Skeleton shimmer (Dawn/Amazon/Airbnb-style) instead of a solid spinner.
      const line = `
        <div class="cart-skeleton__line">
          <div class="cart-skeleton__thumb"></div>
          <div class="cart-skeleton__content">
            <div class="cart-skeleton__title"></div>
            <div class="cart-skeleton__subtitle"></div>
          </div>
          <div class="cart-skeleton__price"></div>
        </div>`;
      overlay.innerHTML =
        '<div class="cart-skeleton" role="status" aria-label="Updating cart">' +
        line + line + line +
        '</div>';
      return overlay;
    }

    _paintSurfaces(busy) {
      const els = document.querySelectorAll(SURFACE_SELECTOR);
      els.forEach((el) => {
        const overlay = el.querySelector(':scope > .cart-loader-overlay');
        if (overlay) overlay.dataset.active = busy ? 'true' : 'false';
      });
    }

    _observeMutations() {
      if (typeof MutationObserver === 'undefined') return;
      // Re-discover surfaces (cart drawer / page can re-render).
      this._observer = new MutationObserver(() => this._refreshSurfaces());
      this._observer.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * Public: hold the overlay visible across a long, multi-fetch operation
     * (e.g. bundle-cart.js cascade removal, which fires N sequential
     * /cart/change.js calls). Between hold() and release() the overlay stays up
     * regardless of the fetch counter bouncing to zero — this kills the flicker.
     */
    hold() {
      this.holdCount += 1;
      if (this._hideTimer) {
        clearTimeout(this._hideTimer);
        this._hideTimer = null;
      }
      this._paintSurfaces(true);
    }

    /** Public: end one hold. Hides once no fetches AND no holds remain. */
    release() {
      this.holdCount = Math.max(0, this.holdCount - 1);
      this._maybeHide();
    }

    /** Hide only when BOTH the fetch counter and the hold counter are at zero. */
    _maybeHide() {
      if (this.activeMutations === 0 && this.holdCount === 0) {
        if (this._hideTimer) clearTimeout(this._hideTimer);
        this._hideTimer = setTimeout(() => {
          this._hideTimer = null;
          if (this.activeMutations === 0 && this.holdCount === 0) {
            this._paintSurfaces(false);
          }
        }, RELEASE_DEBOUNCE_MS);
      }
    }

    _installFetchHook() {
      if (typeof window.fetch !== 'function') return;
      const original = window.fetch.bind(window);
      const self = this;

      window.fetch = async function cartLoaderPatchedFetch(input, options) {
        const url =
          typeof input === 'string'
            ? input
            : input && typeof input.url === 'string'
              ? input.url
              : '';
        const isMutation = CART_MUTATE_RE.test(url);

        if (isMutation) {
          self.activeMutations += 1;
          if (self.activeMutations === 1) {
            self._paintSurfaces(true);
            if (self._hideTimer) {
              clearTimeout(self._hideTimer);
              self._hideTimer = null;
            }
          }
        }

        try {
          return await original(input, options);
        } finally {
          if (isMutation) {
            self.activeMutations = Math.max(0, self.activeMutations - 1);
            self._maybeHide();
          }
        }
      };
    }
  }

  // Expose the instance so bundle-cart.js can hold()/release() the overlay
  // across its multi-fetch cascade.
  function installCartLoader() {
    if (!window.__cartLoader) {
      window.__cartLoader = new CartLoader();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installCartLoader, { once: true });
  } else {
    installCartLoader();
  }

  /**
   * Cart Gift Badge — paint merchant-configured colors onto any
   * .cart-gift-badge node and rewrite the title to the merchant's
   * configured key. Liquid renders the badge against the default
   * 'Gift Included' literal; JS upgrades it after each cart render.
   */
  function applyGiftBadges() {
    const cfg = (typeof window !== 'undefined' && window.__giftPropertyConfig) || null;
    const badges = document.querySelectorAll('[data-gift-badge]');
    if (badges.length === 0) return;

    badges.forEach((el) => {
      if (cfg && cfg.badgeEnabled === false) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      if (cfg) {
        if (cfg.badgeBg) el.style.setProperty('--gift-badge-bg', cfg.badgeBg);
        if (cfg.badgeFg) el.style.setProperty('--gift-badge-fg', cfg.badgeFg);
        if (cfg.key) {
          const title = el.querySelector('.cart-gift-badge__title');
          if (title) title.textContent = cfg.key;
        }
      }
    });
  }

  document.addEventListener('cart:update', applyGiftBadges);
  document.addEventListener('shopify:section:load', applyGiftBadges);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyGiftBadges, { once: true });
  } else {
    applyGiftBadges();
  }
})();


fetch('/cart.js').then(r => r.json()).then(c => console.table(c.items.map(it => ({
  title: it.product_title,
  variant: it.variant_title,
  qty: it.quantity,
  type: it.properties?.['product-type'] || '-',
  bundle_id: it.properties?._bundle_id?.slice(0, 8) || '-',
  key: it.key.slice(0, 12) + '...'
}))));